import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Save, Send, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactFlow, { addEdge, Background, Controls, applyEdgeChanges, applyNodeChanges, MiniMap, Panel, } from 'reactflow';
import 'reactflow/dist/style.css';
import { Button, Card, CardContent, Field, Input, PageHeader, Select, Textarea, useToast, } from '@/components/ui';
import { api } from '@/lib/api';
const NODE_TYPES = [
    { value: 'start', label: 'Start' },
    { value: 'approval', label: 'Approval' },
    { value: 'condition', label: 'Condition' },
    { value: 'action', label: 'Action' },
    { value: 'notification', label: 'Notification' },
    { value: 'end', label: 'End' },
];
export function WorkflowEditorPage() {
    const { id } = useParams();
    const isNew = id === 'new' || !id;
    const navigate = useNavigate();
    const qc = useQueryClient();
    const toast = useToast();
    const [meta, setMeta] = useState({
        name: '', description: '', trigger_event: 'created', target_content_type: 0,
    });
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const existing = useQuery({
        queryKey: ['workflow-def', id],
        queryFn: async () => (await api.get(`/workflows/definitions/${id}/`)).data,
        enabled: !isNew,
    });
    useEffect(() => {
        if (existing.data) {
            setMeta(existing.data);
            setNodes(existing.data.graph?.nodes ?? []);
            setEdges(existing.data.graph?.edges ?? []);
        }
    }, [existing.data]);
    const save = useMutation({
        mutationFn: async () => {
            const payload = { ...meta, graph: { nodes, edges } };
            if (isNew) {
                const res = await api.post('/workflows/definitions/', payload);
                return res.data;
            }
            const res = await api.patch(`/workflows/definitions/${id}/`, payload);
            return res.data;
        },
        onSuccess: (data) => {
            qc.invalidateQueries({ queryKey: ['workflow-defs'] });
            toast.success('Workflow saved');
            if (isNew)
                navigate(`/workflows/definitions/${data.id}`, { replace: true });
        },
        onError: (err) => toast.error('Save failed', JSON.stringify(err.response?.data ?? err.message)),
    });
    const publish = useMutation({
        mutationFn: async () => (await api.post(`/workflows/definitions/${id}/publish/`)).data,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['workflow-def', id] });
            toast.success('Workflow published');
        },
    });
    const onNodesChange = useCallback((changes) => setNodes((ns) => applyNodeChanges(changes, ns)), []);
    const onEdgesChange = useCallback((changes) => setEdges((es) => applyEdgeChanges(changes, es)), []);
    const onConnect = useCallback((conn) => setEdges((es) => addEdge({ ...conn, animated: true }, es)), []);
    const addNode = (type) => {
        const id = `n${Date.now()}`;
        setNodes((ns) => [...ns, {
                id,
                type: 'default',
                data: { label: `${type}: new`, kind: type },
                position: { x: 80 + (ns.length % 5) * 180, y: 80 + Math.floor(ns.length / 5) * 110 },
                style: nodeStyle(type),
            }]);
    };
    const deleteSelected = () => {
        const selectedNodeIds = nodes.filter((n) => n.selected).map((n) => n.id);
        const selectedEdgeIds = edges.filter((e) => e.selected).map((e) => e.id);
        setNodes((ns) => ns.filter((n) => !selectedNodeIds.includes(n.id)));
        setEdges((es) => es.filter((e) => !selectedEdgeIds.includes(e.id) &&
            !selectedNodeIds.includes(e.source) && !selectedNodeIds.includes(e.target)));
    };
    return (_jsxs("div", { className: "space-y-4", children: [_jsx(PageHeader, { title: isNew ? 'New workflow' : `Edit · ${meta.name || '…'}`, description: "Drag nodes from the sidebar, connect them, and publish when ready.", actions: _jsxs("div", { className: "flex gap-2", children: [_jsx(Button, { variant: "outline", leftIcon: _jsx(Save, { className: "h-4 w-4" }), onClick: () => save.mutate(), loading: save.isPending, children: "Save" }), !isNew && (_jsx(Button, { leftIcon: _jsx(Send, { className: "h-4 w-4" }), onClick: () => publish.mutate(), loading: publish.isPending, children: "Publish" }))] }) }), _jsxs("div", { className: "grid grid-cols-12 gap-4", children: [_jsxs("aside", { className: "col-span-12 space-y-3 md:col-span-3", children: [_jsx(Card, { children: _jsxs(CardContent, { className: "space-y-3 p-3", children: [_jsx(Field, { label: "Name", children: _jsx(Input, { value: meta.name ?? '', onChange: (e) => setMeta({ ...meta, name: e.target.value }) }) }), _jsx(Field, { label: "Description", children: _jsx(Textarea, { rows: 2, value: meta.description ?? '', onChange: (e) => setMeta({ ...meta, description: e.target.value }) }) }), _jsx(Field, { label: "Target content type (id)", children: _jsx(Input, { type: "number", value: meta.target_content_type ?? 0, onChange: (e) => setMeta({ ...meta, target_content_type: Number(e.target.value) }) }) }), _jsx(Field, { label: "Trigger event", children: _jsxs(Select, { value: meta.trigger_event ?? 'created', onChange: (e) => setMeta({ ...meta, trigger_event: e.target.value }), children: [_jsx("option", { value: "created", children: "created" }), _jsx("option", { value: "updated", children: "updated" }), _jsx("option", { value: "state_changed", children: "state_changed" }), _jsx("option", { value: "manual", children: "manual" })] }) })] }) }), _jsx(Card, { children: _jsxs(CardContent, { className: "space-y-2 p-3", children: [_jsx("p", { className: "text-xs font-semibold uppercase text-muted-foreground", children: "Add node" }), NODE_TYPES.map((t) => (_jsx(Button, { variant: "outline", size: "sm", className: "w-full justify-start", leftIcon: _jsx(Plus, { className: "h-3 w-3" }), onClick: () => addNode(t.value), children: t.label }, t.value))), _jsx(Button, { variant: "outline", size: "sm", className: "mt-2 w-full justify-start text-red-600", leftIcon: _jsx(Trash2, { className: "h-3 w-3" }), onClick: deleteSelected, children: "Delete selected" })] }) })] }), _jsx("div", { className: "col-span-12 h-[640px] overflow-hidden rounded-lg border bg-card md:col-span-9", children: _jsxs(ReactFlow, { nodes: nodes, edges: edges, onNodesChange: onNodesChange, onEdgesChange: onEdgesChange, onConnect: onConnect, fitView: true, children: [_jsx(Background, { gap: 16 }), _jsx(Controls, {}), _jsx(MiniMap, { pannable: true, zoomable: true }), _jsx(Panel, { position: "top-right", children: _jsxs("span", { className: "rounded bg-card px-2 py-1 text-xs text-muted-foreground", children: [nodes.length, " nodes \u00B7 ", edges.length, " edges"] }) })] }) })] })] }));
}
function nodeStyle(kind) {
    const map = {
        start: { background: '#10b981', color: '#fff', border: '1px solid #047857' },
        approval: { background: '#a855f7', color: '#fff', border: '1px solid #6d28d9' },
        condition: { background: '#f59e0b', color: '#fff', border: '1px solid #b45309' },
        action: { background: '#3b82f6', color: '#fff', border: '1px solid #1e40af' },
        notification: { background: '#0ea5e9', color: '#fff', border: '1px solid #0369a1' },
        end: { background: '#ef4444', color: '#fff', border: '1px solid #991b1b' },
    };
    return { ...map[kind], borderRadius: 8, padding: 4 };
}
